"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import { type ReactNode } from "react";
import Link from "next/link";
import {
  ArrowDown,
  ArrowRight,
  Bot,
  Check,
  History,
  Mail,
  MessageCircle,
  UserRound,
} from "lucide-react";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { Button } from "@/components/platform/v2/ui/button";
//import { VCENTRE_APP_URL as APP_URL } from "@/config/contact";

const appUses = [
  "Задать первый вопрос",
  "Обсудить возможности платформы",
  "Отправить материалы",
  "Вернуться к переписке",
  "Уточнить статус задачи",
  "Обратиться в поддержку",
  "Продолжить разговор с менеджером",
];
const aiUses = [
  "Что такое VCENTRE.ONE",
  "Какой тариф выбрать",
  "Что умеет приложение",
  "Возможности CRM",
  "Сайты и интеграции",
  "Нужный раздел сайта",
];
const routes = [
  ["Хочу VCENTRE.ONE", "Приложение"],
  ["Нужна поддержка", "Приложение / AI"],
  ["Хочу обсудить интеграцию", "Приложение"],
  ["Интересует партнёрство", "Приложение"],
  ["Интересуют инвестиции", "Приложение"],
  ["Официальное обращение", "help@vcentre.pro"],
] as const;

const Card = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => <article className={`surface ${className}`}>{children}</article>;

const ContactsPageComponent = () => {
  const openChat = () =>
    window.dispatchEvent(new CustomEvent("vcentre:open-chat"));
  /*const appAction = APP_URL ? <Button asChild size="lg" variant="site"><a href={APP_URL} target="_blank" rel="noreferrer">Открыть приложение</a></Button> : <Button size="lg" variant="site" onClick={openChat}>Начать диалог</Button>;*/

  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow="Контакты"
        title={
          <>
            Напишите нам.
            <br />
            <span className="text-primary">Мы на связи.</span>
          </>
        }
        text={
          <p>
            Самый удобный способ — открыть приложение VCENTRE и написать там.
            Диалог сохранится, а на быстрые вопросы поможет AI-помощник.
          </p>
        }
        actions={
          <Button asChild size="lg" variant="siteOutline">
            <a href="mailto:help@vcentre.pro">Написать на help@vcentre.pro</a>
          </Button>
        }
        note={
          <button
            type="button"
            onClick={openChat}
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80"
          >
            <MessageCircle className="h-4 w-4" />
            Нужен быстрый ответ? Спросить AI
          </button>
        }
        visual={
          <img
            src={"/assets/images/platform/contacts-mascot-hero.png"}
            alt="Утёнок VCENTRE.IT — всегда на связи"
            width={1254}
            height={1254}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />

      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="eyebrow">Основной канал</p>
            <h2 className="display-2 mt-4">
              Один чат вместо потерянных сообщений.
            </h2>
            <p className="lede mt-5">
              В приложении удобно вести весь диалог по проекту и возвращаться к
              нему без необходимости каждый раз начинать разговор заново.
            </p>
            <div className="mt-7 grid gap-2 sm:grid-cols-2">
              {appUses.map((x) => (
                <div
                  key={x}
                  className="flex gap-2 text-sm text-muted-foreground"
                >
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                  {x}
                </div>
              ))}
            </div>
            {/*APP_URL ? <Button asChild variant="site" className="mt-8"><a href={APP_URL} target="_blank" rel="noreferrer">Перейти в приложение</a></Button> : <Button variant="site" className="mt-8" onClick={openChat}>Начать с AI на сайте</Button>*/}
          </div>
          <Card className="border-primary/30 bg-[hsl(var(--deep-black))]">
            <div className="flex items-center gap-3 border-b border-border/60 pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold">VCENTRE</p>
                <p className="text-xs text-muted-foreground">
                  Диалог по вашему проекту
                </p>
              </div>
              <History className="ml-auto h-5 w-5 text-muted-foreground" />
            </div>
            <div className="mt-6 rounded-lg bg-secondary p-4 text-sm">
              Здравствуйте. Хотим перенести заявки из нескольких каналов в одну
              CRM.
            </div>
            <div className="mt-4 border-l-2 border-primary pl-4 text-sm leading-relaxed">
              Поняли задачу. Расскажите, откуда сейчас приходят заявки и кто их
              обрабатывает — поможем собрать подходящий контур.
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              История диалога остаётся в приложении.
            </p>
          </Card>
        </div>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Одна точка входа"
          title="Быстрый ответ — сразу. Человек — когда нужен."
          text="Если вопрос требует участия команды, разговор продолжается в приложении. Автоматическую передачу контекста с сайта мы пока не обещаем."
        />
        <div className="mx-auto mt-10 grid max-w-5xl gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-center">
          {[
            [MessageCircle, "Вопрос"],
            [Bot, "AI-помощник"],
            [UserRound, "Специалист VCENTRE"],
          ].map(([Icon, title], i) => {
            const I = Icon as typeof Bot;
            return (
              <div key={title as string} className="contents">
                <Card
                  className={
                    i === 1
                      ? "border-primary/40 bg-primary/[0.06] text-center"
                      : "text-center"
                  }
                >
                  <IconHeading
                    as="h3"
                    icon={<I className="mx-auto h-7 w-7 text-primary" />}
                    className="text-lg font-bold"
                  >
                    {title as string}
                  </IconHeading>
                  {i === 1 && (
                    <p className="body-text mt-2">
                      Если нужно — подключается человек.
                    </p>
                  )}
                  {i === 2 && (
                    <p className="body-text mt-2">
                      Диалог продолжается в приложении.
                    </p>
                  )}
                </Card>
                {i < 2 && (
                  <>
                    <ArrowRight className="mx-auto hidden text-primary lg:block" />
                    <ArrowDown className="mx-auto text-primary lg:hidden" />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/*APP_URL && <Section>
          <div className="grid gap-5 lg:grid-cols-2">
            <Card className="border-primary/40 bg-primary/[0.06]"><p className="eyebrow">Нужен быстрый ответ?</p><h2 className="display-3 mt-4">Спросите AI прямо на сайте.</h2><p className="body-text mt-4">Помощник расскажет о платформе, тарифах, приложении, сайтах, CRM, интеграциях и поможет найти нужный раздел.</p><div className="mt-6 flex flex-wrap gap-2">{aiUses.map((x)=><span key={x} className="pill pill-neutral">{x}</span>)}</div><Button variant="site" className="mt-7" onClick={openChat}>Задать вопрос AI</Button></Card>
            <Card><p className="eyebrow">В приложении</p><h2 className="display-3 mt-4">Тот же помощник — внутри приложения.</h2><p className="body-text mt-4">На сайте AI помогает познакомиться с VCENTRE. В приложении — продолжать работу, искать возможности и разбираться в настройках.</p><div className="mt-7 border-l-2 border-primary pl-4"><p className="font-bold">AI помогает быстро. Но человек всегда рядом.</p><p className="body-text mt-2">Проект, техническая проверка, условия, интеграция или нестандартная настройка требуют участия команды.</p></div></Card>
          </div>
         </Section>*/}

      <Section tone="quiet">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <p className="eyebrow">Куда писать</p>
            <h2 className="display-2 mt-4">
              Можно просто написать. Мы сами направим вопрос.
            </h2>
            <div className="mt-8 divide-y divide-border/60 border-y border-border/60">
              {routes.map(([q, a]) => (
                <div
                  key={q}
                  className="grid gap-2 py-4 sm:grid-cols-[1fr_auto] sm:items-center"
                >
                  <p className="text-sm font-bold uppercase">{q}</p>
                  <p className="text-sm text-primary">→ {a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 font-bold">
              Не знаете, кому писать? Просто начните диалог.
            </p>
          </div>
          <div className="grid gap-5">
            <Card>
              <p className="eyebrow mb-3">Email</p>
              <IconHeading
                as="h3"
                icon={<Mail className="h-7 w-7 text-primary" />}
                className="display-3"
              >
                Предпочитаете почту?
              </IconHeading>
              <a
                href="mailto:help@vcentre.pro"
                className="mt-4 block text-xl font-bold text-primary hover:text-primary/80"
              >
                help@vcentre.pro
              </a>
              <p className="body-text mt-4">
                Для официальных обращений, документов, сотрудничества и
                технических вопросов.
              </p>
              <Button asChild variant="siteOutline" className="mt-6">
                <a href="mailto:help@vcentre.pro">Написать письмо</a>
              </Button>
            </Card>
            <Card>
              <h3 className="text-lg font-bold">Уже работаете с платформой?</h3>
              <p className="body-text mt-3">
                Подробнее про AI, живую поддержку и менеджера внедрения.
              </p>
              <Button asChild variant="link" className="mt-3 px-0 text-primary">
                <Link href="/platform/support">
                  Перейти в раздел «Поддержка»{" "}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </Card>
          </div>
        </div>
      </Section>

      <Section>
        <div className="rounded-2xl border border-primary/30 bg-primary/[0.05] p-7 sm:p-10 lg:flex lg:items-center lg:justify-between lg:gap-10">
          <div>
            <p className="eyebrow">Для нового проекта</p>
            <h2 className="display-3 mt-4">
              Хотите понять, подойдёт ли VCENTRE.ONE вашему бизнесу?
            </h2>
            <p className="body-text mt-4 max-w-3xl">
              Не обязательно заранее знать тариф или состав проекта. Расскажите,
              как сейчас работает бизнес — поможем понять, какой контур имеет
              смысл собрать.
            </p>
          </div>
          <Button
            asChild
            size="lg"
            variant="site"
            className="mt-7 shrink-0 lg:mt-0"
          >
            <Link href="/signin?source=contacts">
              Собрать концепт моего проекта
            </Link>
          </Button>
        </div>
      </Section>

      <Section>
        <PageCta
          eyebrow="Начните с сообщения"
          title="Не нужен длинный бриф."
          text={
            <p>
              Расскажите своими словами, что хотите решить. Продолжим диалог в
              удобном формате.
            </p>
          }
          actions={
            <Button asChild size="lg" variant="siteOutline">
              <a href="mailto:help@vcentre.pro">help@vcentre.pro</a>
            </Button>
          }
          visual={
            <img
              src={"/assets/images/platform/contacts-mascot-cta.png"}
              alt="Утёнок VCENTRE.IT — запускаем быстро"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
    </div>
  );
};

export default ContactsPageComponent;
